import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Clock, BookOpen } from "lucide-react";
import { EBOOK, COMING_SOON_EBOOKS } from "@/data/ebookData";

export default function LibraryPage() {
  useEffect(() => {
    document.title = "Library | The AI Mastery Series by Shadab Rashid";
  }, []);
  return (
    <div data-testid="library-page" className="min-h-screen">
      {/* Header */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 pt-12 lg:pt-20 pb-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <span className="font-mono-plex text-xs tracking-[0.2em] uppercase text-[#666666]">
            The AI Mastery Series
          </span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter mt-4 font-cabinet text-[#0A0A0A] leading-none">
            Library
          </h1>
          <p className="text-base md:text-lg text-[#666666] mt-4 max-w-xl leading-relaxed font-plex">
            Enterprise-level AI education. Each book builds on the last, taking you from foundational understanding to practical mastery.
          </p>
        </motion.div>
      </section>

      {/* Published Ebook */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-8">
        <span className="font-mono-plex text-xs tracking-[0.2em] uppercase text-[#666666]">
          Available Now
        </span>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="mt-6"
        >
          <Link
            to="/read/what-ai-is-not"
            className="block border border-[#E5E5E5] bg-white hover:border-[#0A0A0A] transition-colors group"
            data-testid="library-ebook-main"
          >
            <div className="grid grid-cols-1 md:grid-cols-12 gap-0">
              <div className="md:col-span-4 lg:col-span-3">
                <div className="aspect-[3/4] md:h-full bg-[#0A0A0A] flex flex-col justify-between p-5 md:p-6">
                  <p className="font-mono-plex text-[8px] tracking-[0.25em] uppercase text-[#FF4F00]">The AI Mastery Series</p>
                  <div>
                    <h3 className="font-cabinet font-bold text-2xl md:text-3xl text-white leading-none tracking-tighter">What AI<br/>Is <span className="text-[#FF4F00]">NOT</span></h3>
                    <div className="w-8 h-[2px] bg-[#FF4F00] mt-3 mb-3" />
                    <p className="font-mono-plex text-[7px] tracking-[0.12em] uppercase text-white/40 mb-0.5">Thought by</p>
                    <p className="font-cabinet font-bold text-base md:text-lg text-white tracking-tight">Shadab Rashid</p>
                    <p className="font-mono-plex text-[6px] tracking-[0.12em] uppercase text-white/40 mt-2 mb-0.5">Compiled &amp; Curated by</p>
                    <p className="font-cabinet font-medium text-xs text-white/70 tracking-tight">Shifa Shaikh</p>
                  </div>
                </div>
              </div>
              <div className="md:col-span-8 lg:col-span-9 p-6 md:p-8 lg:p-12 flex flex-col justify-center">
                <div className="flex items-center gap-3 mb-4">
                  <span className="bg-[#FF4F00] text-white text-[10px] font-bold font-mono-plex tracking-[0.15em] uppercase px-3 py-1">
                    Book 01
                  </span>
                  <span className="bg-[#0A0A0A] text-white text-[10px] font-bold font-mono-plex tracking-[0.15em] uppercase px-3 py-1">
                    Published
                  </span>
                </div>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight font-cabinet text-[#0A0A0A]">
                  {EBOOK.title}
                </h2>
                <p className="text-base text-[#666666] mt-3 leading-relaxed font-plex max-w-xl">
                  {EBOOK.subtitle}. Twenty chapters that cut through the noise and give you the honest understanding of AI that most books are too afraid to write.
                </p>

                <div className="flex flex-wrap gap-6 mt-6 py-4 border-t border-[#E5E5E5]">
                  <div className="flex items-center gap-2">
                    <BookOpen size={14} className="text-[#666666]" />
                    <span className="text-sm text-[#666666] font-mono-plex">{EBOOK.stats.chapters} Chapters</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock size={14} className="text-[#666666]" />
                    <span className="text-sm text-[#666666] font-mono-plex">{EBOOK.stats.pages} Pages</span>
                  </div>
                </div>

                <div className="mt-6 flex items-center gap-2 text-[#FF4F00] text-sm font-medium group-hover:gap-3 transition-all">
                  Start Reading <ArrowRight size={16} />
                </div>
              </div>
            </div>
          </Link>
        </motion.div>
      </section>

      {/* Coming Soon */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-8 pb-16 lg:pb-24">
        <span className="font-mono-plex text-xs tracking-[0.2em] uppercase text-[#666666]">
          Coming Soon
        </span>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
          {COMING_SOON_EBOOKS.map((book, i) => (
            <motion.div
              key={book.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 + i * 0.1 }}
              className="border border-[#E5E5E5] bg-white p-6 md:p-8 relative overflow-hidden"
              data-testid={`library-ebook-${book.id}`}
            >
              <div className="absolute top-0 right-0 bg-[#F0F0F0] text-[#666666] text-[10px] font-bold font-mono-plex tracking-[0.15em] uppercase px-3 py-1">
                Coming Soon
              </div>
              <span className="font-mono-plex text-xs tracking-[0.15em] text-[#FF4F00] font-bold">
                Book {String(i + 2).padStart(2, "0")}
              </span>
              <h3 className="font-cabinet font-bold text-xl mt-3 text-[#0A0A0A]">
                {book.title}
              </h3>
              <p className="text-sm text-[#666666] mt-2 leading-relaxed font-plex">
                {book.subtitle}
              </p>
              <div className="flex gap-4 mt-4 pt-4 border-t border-[#E5E5E5]">
                <span className="text-xs text-[#666666] font-mono-plex">{book.chapters} Chapters</span>
                <span className="text-xs text-[#666666] font-mono-plex">{book.pages} Pages</span>
              </div>
            </motion.div>
          ))}
        </div>
      </section>
    </div>
  );
}
